import { config } from '@/config'
import { AssessmentStepKeys, AssessmentType, Categories } from '@/enums'
import { useAuth } from '@/providers'
import { paths } from '@/routes/paths'
import { useCreateAccountV2 } from '@/services'
import {
  deleteLocalStorageItems,
  extractFunnelFromPathname,
  goToExternalUrl,
  pushEventToDataLayer,
  setLocalStorageItem,
  useGoToPath,
  useLanguage,
  useModal,
  usePrevious,
} from '@/utils'
import { identity, last } from 'ramda'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AssessmentStepper } from '../../AssessmentStepper'
import {
  getStoredAssessmentValues,
  getStoredCurrentStep,
  makeStoredAssessmentValuesKey,
  makeStoredCurrentStepKey,
  useForwardFunnelSource,
} from '../../helpers'
import { useAssessmentSteps } from '../../steps/hooks/useAssessmentSteps'
import { MockRecommendation } from '../MockRecommendation'

type CategoryId = keyof typeof Categories

interface PropsMessages {
  helperText: string
  steps: string[]
}

interface Messages {
  propsMessages: PropsMessages
  subtitle: string
  subtitles: Record<string, string>
  titles: Record<string, string>
  assessmentSuccessButton: string
  assessmentSuccessSubtitle: string
  assessmentSuccessTitle: string
}

interface ConditionalOfferAssessmentProps {
  onLogoClick?: () => void
  categoryId: CategoryId
  stepKeys: string[]
  makeStepProps: (propsMessages: PropsMessages) => Record<string, any>
  stepperBreadcrumbTitles?: Record<string, any>
  transformSubmitValues?: <T>(values: T) => T
}

const useMessages = (): Messages => {
  const { t } = useTranslation()

  return {
    propsMessages: {
      helperText: t('approxHelperText'),
      steps: [
        t('assessmentLoadingStep1'),
        t('liabilityAssessmentLoadingStep2'),
        t('liabilityAssessmentLoadingStep3'),
        t('assessmentLoadingStep5'),
      ],
    },
    subtitle: t('leadStepSubtitle'),
    subtitles: {
      [AssessmentStepKeys.businessTypeAi]: t(
        'selfAssessment.locationType.businessTypeSubtitleAi',
      ),
      [AssessmentStepKeys.businessTypeMainSelectionAi]: t(
        'selfAssessment.locationType.businessTypeMainSelectionAiSubtitle',
      ),
    },
    titles: {
      [AssessmentStepKeys.businessType]: t('businessTypeTitle'),
      [AssessmentStepKeys.businessTypeAi]: t(
        'selfAssessment.locationType.businessTypeTitleAi',
      ),
      [AssessmentStepKeys.businessTypeConfirmationAi]: t(
        'selfAssessment.locationType.businessTypeConfirmationAiTitle',
      ),
      [AssessmentStepKeys.businessTypeMainSelectionAi]: t(
        'selfAssessment.locationType.businessTypeMainSelectionAiTitle',
      ),
      [AssessmentStepKeys.companyDetails]: t('companyDetailsTitle'),
      [AssessmentStepKeys.companyFoundedDateLead]: t('companyFoundedDateTitle'),
      [AssessmentStepKeys.employeesCount]: t('employeesCountTitle'),
      [AssessmentStepKeys.approxRevenue]: t('approxRevenueTitle'),
      [AssessmentStepKeys.inventory]: t('inventoryTitle'),
      [AssessmentStepKeys.approxLaborCosts]: t('approxLaborCostsTitle'),
      [AssessmentStepKeys.prepareData]: t('preparingOffer'),
      [AssessmentStepKeys.prepareOfferB]: t('preparingOffer'),
      [AssessmentStepKeys.contactData]: t('createLeadTitle'),
      [AssessmentStepKeys.createAccount]: t('createAccountTitle'),
      [AssessmentStepKeys.createAccountB]: t('createAccountTitle'),
      [AssessmentStepKeys.startDate]: t('startDateTitle'),
      [AssessmentStepKeys.costOfElectronics]: t('costOfElectronicsTitle'),
    },
    assessmentSuccessButton: t('common.continue'),
    assessmentSuccessSubtitle: t('common.success'),
    assessmentSuccessTitle: t('common.success'),
  }
}

const additionalSteps = [
  AssessmentStepKeys.prepareOfferB,
  AssessmentStepKeys.contactData,
  AssessmentStepKeys.createAccountB,
]

const additionalStepsB = [
  AssessmentStepKeys.prepareOfferB,
  AssessmentStepKeys.contactData,
  AssessmentStepKeys.createAccountB,
]

export const ConditionalOfferAssessment = ({
  onLogoClick,
  categoryId,
  stepKeys,
  makeStepProps,
  stepperBreadcrumbTitles,
  transformSubmitValues = identity,
}: ConditionalOfferAssessmentProps) => {
  const language = useLanguage()
  const { isAuthenticated } = useAuth()
  const hasBreadcrumbs = !!stepperBreadcrumbTitles
  const messages = useMessages()

  const prevCategoryId = usePrevious(categoryId) as string

  const stepLocalStorageKey = makeStoredCurrentStepKey(categoryId) as string
  const valuesLocalStorageKey = makeStoredAssessmentValuesKey(
    categoryId,
  ) as string

  const createAccountMutation = useCreateAccountV2(categoryId)

  const funnelProduct = extractFunnelFromPathname()
  const queryParams = useForwardFunnelSource()

  const aggregatedStepKeys = [
    ...stepKeys,
    ...(hasBreadcrumbs ? additionalStepsB : additionalSteps),
  ]

  const steps = useAssessmentSteps({
    categoryId,
    stepKeys: aggregatedStepKeys,
  })

  const goToRecommendation = useGoToPath(
    paths.viewSsfOfferWithKey(categoryId),
    // when funnel source is not a concern (new tracking fully set up), this will be
    // transformed to just forward the funnel_product instead of spreading the query params
    { ...queryParams, funnel_product: funnelProduct },
  )

  const recommendationState = useModal()

  const handleCalculationSuccessful = () => {
    recommendationState.open()
    pushEventToDataLayer('Step Change', {
      funnel_step: 'contact_data_recommendation',
      funnel_product: extractFunnelFromPathname(),
    })
  }

  const handleFinish = () => {
    goToRecommendation()
    pushEventToDataLayer('Step Change', {
      funnel_step: 'recommendation_screen',
      funnel_product: funnelProduct,
    })

    recommendationState.close()
  }

  const receivedStepProps = useMemo(() => {
    return makeStepProps(messages.propsMessages)
  }, [makeStepProps, messages.propsMessages])

  const stepProps = useMemo(
    () => ({
      ...receivedStepProps,
      [AssessmentStepKeys.businessTypeAi]: {
        ...receivedStepProps[AssessmentStepKeys.businessTypeAi],
        categoryId,
      },
      [AssessmentStepKeys.createAccount]: {
        categoryId,
        goToStepName: last(stepKeys),
        mutation: createAccountMutation,
        transformSubmitValues,
      },
      [AssessmentStepKeys.createAccountB]: {
        categoryId,
        mutation: createAccountMutation,
        transformSubmitValues,
      },
      [AssessmentStepKeys.contactData]: {
        categoryId,
        handleCalculationSuccessful,
        transformSubmitValues,
        onFinish: handleFinish,
      },
      [AssessmentStepKeys.prepareData]: {
        stepsMessages: messages.propsMessages.steps,
        hideButtons: true,
      },
      [AssessmentStepKeys.prepareOfferB]: {
        hideButtons: true,
        categoryId,
        transformSubmitValues,
      },
    }),
    [language, categoryId, JSON.stringify(createAccountMutation), stepKeys],
  )

  useEffect(() => {
    if (isAuthenticated) goToExternalUrl(`https://${config.appDomain}`)
  }, [])

  // update stored values keys
  useEffect(() => {
    if (prevCategoryId && categoryId !== prevCategoryId) {
      const prevAssessmentValues = getStoredAssessmentValues(prevCategoryId)
      const prevCurrentStep = getStoredCurrentStep(prevCategoryId)

      // Use direct localStorage operations to avoid helper function type issues
      setLocalStorageItem(valuesLocalStorageKey, prevAssessmentValues)
      setLocalStorageItem(stepLocalStorageKey, prevCurrentStep)

      const keysToDelete = [
        makeStoredAssessmentValuesKey(prevCategoryId),
        makeStoredCurrentStepKey(prevCategoryId),
      ]

      deleteLocalStorageItems(keysToDelete as string[])
    }
  }, [categoryId])

  if (recommendationState.isOpen) {
    return (
      <MockRecommendation
        categoryId={categoryId}
        onBack={recommendationState.close}
        onFinish={handleFinish}
        transformSubmitValues={transformSubmitValues}
      />
    )
  }

  const rawCurrentStep = getStoredCurrentStep(categoryId)
  const rawCurrentValues = getStoredAssessmentValues(categoryId)

  const currentStep =
    typeof rawCurrentStep === 'string' ? rawCurrentStep : undefined
  const currentValues =
    rawCurrentValues && typeof rawCurrentValues === 'object'
      ? (rawCurrentValues as Record<string, any>)
      : {}

  return (
    <AssessmentStepper
      initialStep={currentStep}
      initialValues={currentValues}
      messages={messages}
      onFinish={handleFinish}
      onLogoClick={onLogoClick}
      skipSuccessPage
      stepKeys={steps}
      stepLocalStorageKey={stepLocalStorageKey}
      stepperBreadcrumbTitles={stepperBreadcrumbTitles}
      stepProps={stepProps}
      type={AssessmentType.firstTimeUser}
      valuesLocalStorageKey={valuesLocalStorageKey}
    />
  )
}
